/**
 * Reads from and writes to the device replica.
 *
 * Reads return the very same shapes the REST API does (lib/types), so the screens don't know or
 * care whether a list came from the network or from this database. The server stays the source of
 * truth: everything here is a copy that a sync overwrites.
 */
import type {
  Account,
  AccountClass,
  Category,
  CategoryType,
  Merchant,
  Posting,
  Transaction,
  TransactionDetail,
  TransactionType,
} from "@/lib/types";
import { fromUnits, negateMoney, sumMoney, toUnits } from "./decimal";
import type { Row, SqlDriver, SqlParam, Statement } from "./types";

// --- the change feed (server -> device) ------------------------------------------------------

export type SyncEntity = "account" | "category" | "merchant" | "budget" | "transaction";

export interface SyncChange {
  seq: number;
  // The server's entity set is wider than this device replica's (e.g. Fitness entities are
  // pull-synced but this app version has nowhere to put them - see applyPage). Kept as `string`,
  // not `SyncEntity`: that union is what THIS CLIENT understands, not a promise about what a
  // future server version can send down the same feed.
  entity: string;
  id: string;
  op: "upsert" | "delete";
  data: Record<string, unknown> | null;
}

export interface PullPage {
  changes: SyncChange[];
  next_cursor: number;
  has_more: boolean;
  head: number;
}

const TABLES: Record<SyncEntity, { table: string; columns: readonly string[] }> = {
  account: {
    table: "accounts",
    columns: [
      "id", "name", "account_type", "account_class", "currency", "opening_balance", "opening_date",
      "institution", "notes", "is_active", "deleted_at", "version", "balance", "created_at", "updated_at",
    ],
  },
  category: {
    table: "categories",
    columns: [
      "id", "name", "category_type", "parent_id", "icon", "is_active", "is_default", "sort_order",
      "deleted_at", "version", "updated_at",
    ],
  },
  merchant: {
    table: "merchants",
    columns: ["id", "name", "normalised_name", "default_category_id", "deleted_at", "version", "updated_at"],
  },
  budget: {
    table: "budgets",
    columns: ["id", "category_id", "period_type", "period_start", "amount", "currency", "version", "updated_at"],
  },
  transaction: {
    table: "transactions",
    columns: [
      "id", "transaction_type", "transaction_date", "amount", "currency", "account_id",
      "counter_account_id", "category_id", "adjustment_direction", "merchant_id", "description",
      "reference", "notes", "original_transaction_id", "import_batch_id", "voided_at", "void_reason",
      "version", "created_at", "updated_at", "postings", "merchant_name",
    ],
  },
};

function toParam(value: unknown): SqlParam {
  if (value === undefined || value === null) return null;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "string" || typeof value === "number" || typeof value === "bigint") return value;
  return JSON.stringify(value); // e.g. a transaction's postings
}

/**
 * True while the user has a change to this transaction that the server has not answered yet.
 * The local copy then holds *their* version, and a server copy must not overwrite it: the push
 * result (applied, or a conflict carrying the server's version) settles which one wins.
 */
const HAS_UNSENT_EDIT = (id: string) =>
  `EXISTS (SELECT 1 FROM outbox o WHERE o.entity_id = ${id} AND o.status IN ('pending', 'sending'))`;

/** Insert-or-replace a server entity. Never replaces a transaction the user has unsent edits to. */
export function upsert(entity: SyncEntity, data: Record<string, unknown>): Statement {
  const { table, columns } = TABLES[entity];
  const updates = columns.filter((c) => c !== "id").map((c) => `${c} = excluded.${c}`);
  const guard = entity === "transaction" ? ` WHERE NOT ${HAS_UNSENT_EDIT("excluded.id")}` : "";
  return {
    sql: `INSERT INTO ${table} (${columns.join(", ")}) VALUES (${columns.map(() => "?").join(", ")})
          ON CONFLICT(id) DO UPDATE SET ${updates.join(", ")}${guard}`,
    params: columns.map((c) => toParam(data[c])),
  };
}

export const setMeta = (key: string, value: string): Statement => ({
  sql: `INSERT INTO meta (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
  params: [key, value],
});

export async function getMeta(driver: SqlDriver, key: string): Promise<string | null> {
  const rows = await driver.query<{ value: string }>(`SELECT value FROM meta WHERE key = ?`, [key]);
  return rows[0]?.value ?? null;
}

export const putMeta = (driver: SqlDriver, key: string, value: string) =>
  driver.batch([setMeta(key, value)]);

export async function readCursor(driver: SqlDriver): Promise<number> {
  return Number((await getMeta(driver, "cursor")) ?? 0);
}

/**
 * Applies one page of the change feed. The rows and the new cursor commit together, so being
 * killed mid-sync (app closed, phone dies) can never leave a cursor that claims changes the
 * database does not hold - the next sync simply resumes from the last committed page.
 */
export async function applyPage(driver: SqlDriver, page: PullPage): Promise<void> {
  // Changes for an entity this device version does not store (see SyncChange.entity) are simply
  // not applied - the cursor still advances past them, same as any change this device made no
  // local copy of would be handled. Nothing is lost: the server is the source of truth, and a
  // screen that needs that entity reads it over the network instead (see lib/queries.ts).
  const statements: Statement[] = page.changes
    .filter((change): change is SyncChange & { entity: SyncEntity } => change.entity in TABLES)
    .map((change) =>
      change.op === "delete" || change.data === null
        ? { sql: `DELETE FROM ${TABLES[change.entity].table} WHERE id = ?`, params: [change.id] }
        : upsert(change.entity, change.data),
    );
  statements.push(setMeta("cursor", String(page.next_cursor)));
  // Operations the server has applied stop counting towards the provisional balance the moment
  // the cursor reaches them - in this same commit, so no reader ever sees them counted twice.
  statements.push({
    sql: `DELETE FROM outbox WHERE status = 'acked' AND acked_head <= ?`,
    params: [page.next_cursor],
  });
  await driver.batch(statements);
}

// --- row helpers -----------------------------------------------------------------------------

const str = (v: unknown): string => String(v ?? "");
const strOrNull = (v: unknown): string | null => (v === null || v === undefined ? null : String(v));
const flag = (v: unknown): boolean => Number(v) === 1;

// --- reads: accounts, categories, merchants --------------------------------------------------

/**
 * What the user's own not-yet-confirmed operations do to each account: the sum of their exact
 * effects, and how many operations that is. Counts everything the server has not yet folded into
 * the balance it sent us - queued, in flight, and applied-but-not-yet-pulled - and nothing the
 * server refused.
 */
export async function pendingByAccount(
  driver: SqlDriver,
): Promise<Map<string, { delta: bigint; count: number }>> {
  const rows = await driver.query<{ effects: string }>(
    `SELECT effects FROM outbox
      WHERE status IN ('pending', 'sending')
         OR (status = 'acked'
             AND acked_head > COALESCE((SELECT CAST(value AS INTEGER) FROM meta WHERE key = 'cursor'), 0))`,
  );
  const totals = new Map<string, { delta: bigint; count: number }>();
  for (const row of rows) {
    for (const [accountId, amount] of Object.entries(JSON.parse(row.effects) as Record<string, string>)) {
      const entry = totals.get(accountId) ?? { delta: 0n, count: 0 };
      totals.set(accountId, { delta: entry.delta + toUnits(amount), count: entry.count + 1 });
    }
  }
  return totals;
}

export async function listAccounts(driver: SqlDriver): Promise<Account[]> {
  const pending = await pendingByAccount(driver);
  const rows = await driver.query(
    `SELECT a.*, EXISTS (
        SELECT 1 FROM transactions t WHERE t.account_id = a.id OR t.counter_account_id = a.id
      ) AS has_transactions
       FROM accounts a WHERE a.deleted_at IS NULL ORDER BY a.account_class, a.name`,
  );
  return rows.map((r) => {
    const mine = pending.get(str(r.id));
    const serverBalance = str(r.balance);
    return {
      id: str(r.id),
      name: str(r.name),
      account_type: str(r.account_type),
      account_class: str(r.account_class) as AccountClass,
      currency: str(r.currency),
      opening_balance: str(r.opening_balance),
      opening_date: str(r.opening_date),
      // The one place the client computes a balance (spec 3.7, by decision): the server's figure
      // plus this device's own unconfirmed operations, in exact arithmetic, and labelled pending.
      balance: mine ? fromUnits(toUnits(serverBalance) + mine.delta) : serverBalance,
      synced_balance: serverBalance,
      pending_count: mine?.count ?? 0,
      institution: strOrNull(r.institution),
      notes: strOrNull(r.notes),
      is_active: flag(r.is_active),
      has_transactions: flag(r.has_transactions),
      created_at: str(r.created_at),
      updated_at: str(r.updated_at),
      version: Number(r.version),
    };
  });
}

export async function listCategories(driver: SqlDriver): Promise<Category[]> {
  const rows = await driver.query(
    `SELECT c.*, (
        EXISTS (SELECT 1 FROM transactions t WHERE t.category_id = c.id)
        OR EXISTS (SELECT 1 FROM budgets b WHERE b.category_id = c.id)
      ) AS in_use
       FROM categories c WHERE c.deleted_at IS NULL
       ORDER BY c.category_type, c.sort_order, c.name`,
  );
  return rows.map((r) => ({
    id: str(r.id),
    name: str(r.name),
    category_type: str(r.category_type) as CategoryType,
    parent_id: strOrNull(r.parent_id),
    icon: strOrNull(r.icon),
    is_active: flag(r.is_active),
    is_default: flag(r.is_default),
    sort_order: Number(r.sort_order),
    in_use: flag(r.in_use),
    version: Number(r.version),
  }));
}

/** Same normalisation the server applies to merchant names (app/services/merchants.py). */
export const normaliseName = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim().slice(0, 120);

const escapeLike = (text: string): string => text.replace(/[\\%_]/g, (m) => `\\${m}`);

export async function searchMerchants(driver: SqlDriver, q: string, limit: number): Promise<Merchant[]> {
  const key = normaliseName(q);
  const rows = key
    ? await driver.query(
        `SELECT * FROM merchants WHERE deleted_at IS NULL AND normalised_name LIKE ? ESCAPE '\\'
         ORDER BY name LIMIT ?`,
        [`%${escapeLike(key)}%`, limit],
      )
    : await driver.query(`SELECT * FROM merchants WHERE deleted_at IS NULL ORDER BY name LIMIT ?`, [limit]);
  return rows.map((r) => ({
    id: str(r.id),
    name: str(r.name),
    normalised_name: str(r.normalised_name),
    default_category_id: strOrNull(r.default_category_id),
    version: Number(r.version),
  }));
}

// --- reads: transactions ---------------------------------------------------------------------

const TRANSACTION_TYPES = new Set(["income", "expense", "transfer", "refund", "adjustment"]);

const SORT_SQL: Record<string, string> = {
  date: "t.transaction_date",
  // Ordering only: a double is precise to ~15 digits, far beyond any real transaction. The value
  // shown is always the exact decimal string.
  amount: "CAST(t.amount AS REAL)",
  created: "t.created_at",
  id: "t.id",
};

/** Mirrors the server's parse_sort: date/amount/created, then created and id as tiebreakers. */
function orderBy(sort: string | undefined): string {
  const keys: [string, boolean][] = [];
  for (const token of (sort ?? "-date").split(",")) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const descending = trimmed.startsWith("-");
    const name = trimmed.replace(/^[+-]+/, "");
    if (!(name in SORT_SQL) || name === "id") continue; // unknown keys are ignored, never interpolated
    if (!keys.some(([k]) => k === name)) keys.push([name, descending]);
  }
  if (keys.length === 0) keys.push(["date", true]);
  if (!keys.some(([k]) => k === "created")) keys.push(["created", true]);
  keys.push(["id", true]);
  return keys.map(([name, desc]) => `${SORT_SQL[name]} ${desc ? "DESC" : "ASC"}`).join(", ");
}

const TRANSACTION_SELECT = `
  SELECT t.*, a.name AS account_name, ca.name AS counter_account_name,
         c.name AS category_name, c.icon AS category_icon,
         COALESCE(m.name, t.merchant_name) AS merchant_display,
         ${HAS_UNSENT_EDIT("t.id")} AS is_pending
    FROM transactions t
    LEFT JOIN accounts ca ON ca.id = t.counter_account_id
    LEFT JOIN accounts a ON a.id = t.account_id
    LEFT JOIN categories c ON c.id = t.category_id
    LEFT JOIN merchants m ON m.id = t.merchant_id`;

/** Effect of a transaction on one account's balance (debit-normal), as an exact decimal string. */
export function effectOnAccount(
  row: { transaction_type: string; amount: string; counter_account_id: string | null; adjustment_direction: string | null },
  accountId: string,
): string {
  const amount = fromUnits(toUnits(row.amount));
  switch (row.transaction_type) {
    case "transfer":
      return accountId === row.counter_account_id ? amount : negateMoney(amount);
    case "income":
    case "refund":
      return amount;
    case "expense":
      return negateMoney(amount);
    default:
      return row.adjustment_direction === "increase" ? amount : negateMoney(amount);
  }
}

/** A transaction's effect on every account it touches: {accountId: signed exact decimal}. */
export function involvedEffects(row: {
  transaction_type: string;
  amount: string;
  account_id: string;
  counter_account_id: string | null;
  adjustment_direction: string | null;
}): Record<string, string> {
  const effects: Record<string, string> = { [row.account_id]: effectOnAccount(row, row.account_id) };
  if (row.counter_account_id) effects[row.counter_account_id] = effectOnAccount(row, row.counter_account_id);
  return effects;
}

function toTransaction(row: Row, perspective?: string): Transaction {
  const accountId = str(row.account_id);
  const counterId = strOrNull(row.counter_account_id);
  const categoryId = strOrNull(row.category_id);
  const merchantId = strOrNull(row.merchant_id);
  const base = {
    transaction_type: str(row.transaction_type),
    amount: str(row.amount),
    counter_account_id: counterId,
    adjustment_direction: strOrNull(row.adjustment_direction),
  };
  return {
    id: str(row.id),
    transaction_type: base.transaction_type as TransactionType,
    transaction_date: str(row.transaction_date),
    amount: base.amount,
    signed_amount: effectOnAccount(base, perspective ?? accountId),
    currency: str(row.currency),
    account: { id: accountId, name: str(row.account_name ?? "Account") },
    counter_account: counterId ? { id: counterId, name: str(row.counter_account_name ?? "Account") } : null,
    category: categoryId
      ? { id: categoryId, name: str(row.category_name ?? "Category"), icon: strOrNull(row.category_icon) }
      : null,
    // A merchant typed offline has a name but no id until the server creates it.
    merchant: merchantId
      ? { id: merchantId, name: str(row.merchant_display ?? "") }
      : row.merchant_display
        ? { id: "", name: str(row.merchant_display) }
        : null,
    description: str(row.description),
    reference: strOrNull(row.reference),
    notes: strOrNull(row.notes),
    adjustment_direction: base.adjustment_direction as Transaction["adjustment_direction"],
    original_transaction_id: strOrNull(row.original_transaction_id),
    import_batch_id: strOrNull(row.import_batch_id),
    is_voided: row.voided_at !== null && row.voided_at !== undefined,
    voided_at: strOrNull(row.voided_at),
    void_reason: strOrNull(row.void_reason),
    // Reconciliations and attachments are not part of the replica; they are known only online.
    is_reconciled: false,
    attachment_count: 0,
    created_at: str(row.created_at),
    updated_at: str(row.updated_at),
    version: Number(row.version),
    is_pending: flag(row.is_pending),
  };
}

export interface TransactionPage {
  rows: Transaction[];
  /** Offset to pass back for the next page, or null when this was the last one. */
  nextOffset: number | null;
}

export async function listTransactions(
  driver: SqlDriver,
  params: Readonly<Record<string, string>>,
  { limit = 50, offset = 0 }: { limit?: number; offset?: number } = {},
): Promise<TransactionPage> {
  const where: string[] = [];
  const args: SqlParam[] = [];

  if (params.status === "voided") where.push("t.voided_at IS NOT NULL");
  else if (params.status !== "all") where.push("t.voided_at IS NULL");

  if (params.date_from) { where.push("t.transaction_date >= ?"); args.push(params.date_from); }
  if (params.date_to) { where.push("t.transaction_date <= ?"); args.push(params.date_to); }
  if (params.account_id) {
    where.push("(t.account_id = ? OR t.counter_account_id = ?)");
    args.push(params.account_id, params.account_id);
  }
  if (params.category_id) {
    // A category and its children (categories nest one level).
    where.push("t.category_id IN (SELECT id FROM categories WHERE id = ? OR parent_id = ?)");
    args.push(params.category_id, params.category_id);
  }
  if (params.merchant_id) { where.push("t.merchant_id = ?"); args.push(params.merchant_id); }
  if (params.import_batch_id) { where.push("t.import_batch_id = ?"); args.push(params.import_batch_id); }

  const types = (params.type ?? "").split(",").map((s) => s.trim()).filter((s) => TRANSACTION_TYPES.has(s));
  if (types.length > 0) {
    where.push(`t.transaction_type IN (${types.map(() => "?").join(", ")})`);
    args.push(...types);
  }
  if (params.min_amount) { where.push("CAST(t.amount AS REAL) >= CAST(? AS REAL)"); args.push(params.min_amount); }
  if (params.max_amount) { where.push("CAST(t.amount AS REAL) <= CAST(? AS REAL)"); args.push(params.max_amount); }

  const q = params.q?.trim();
  if (q) {
    const like = `%${escapeLike(q)}%`;
    const conditions = [
      "t.description LIKE ? ESCAPE '\\'",
      "t.reference LIKE ? ESCAPE '\\'",
      "t.notes LIKE ? ESCAPE '\\'",
      "m.name LIKE ? ESCAPE '\\'",
      "c.name LIKE ? ESCAPE '\\'",
    ];
    args.push(like, like, like, like, like);
    const asAmount = q.replace(/,/g, "");
    if (/^-?\d+(\.\d+)?$/.test(asAmount)) {
      conditions.push("CAST(t.amount AS REAL) = CAST(? AS REAL)");
      args.push(asAmount);
    }
    where.push(`(${conditions.join(" OR ")})`);
  }

  const rows = await driver.query(
    `${TRANSACTION_SELECT}
     ${where.length ? `WHERE ${where.join(" AND ")}` : ""}
     ORDER BY ${orderBy(params.sort)}
     LIMIT ? OFFSET ?`,
    [...args, limit + 1, offset],
  );
  const hasMore = rows.length > limit;
  return {
    rows: rows.slice(0, limit).map((r) => toTransaction(r, params.account_id)),
    nextOffset: hasMore ? offset + limit : null,
  };
}

export async function getTransactionDetail(driver: SqlDriver, id: string): Promise<TransactionDetail | null> {
  const rows = await driver.query(`${TRANSACTION_SELECT} WHERE t.id = ?`, [id]);
  const row = rows[0];
  if (!row) return null;
  const txn = toTransaction(row);

  const postings: Posting[] = row.postings ? (JSON.parse(String(row.postings)) as Posting[]) : [];

  let refunded: string | null = null;
  let refundIds: string[] = [];
  if (txn.transaction_type === "expense") {
    const refunds = await driver.query<{ id: string; amount: string }>(
      `SELECT id, amount FROM transactions WHERE original_transaction_id = ? AND voided_at IS NULL`,
      [id],
    );
    refunded = sumMoney(refunds.map((r) => r.amount));
    refundIds = refunds.map((r) => r.id);
  }
  return { ...txn, postings, attachments: [], refunded_amount: refunded, refund_ids: refundIds };
}
